<template>
	<Popover ref="popover">
		<div
			class="record-button"
			title="Recording Options"
			:class="{ recording: isRecording }"
			:aria-label="$t('meetings_recordstatus_title')"
			tabindex="0"
			role="button"
		>
			<RecordVideoIcon class="record-icon" />
			<div
				class="record-text"
				:class="{ publishing: isPublishing }"
				v-t="'meetings_recordstatus_title'"
			/>
		</div>
		<template #content>
			<div class="menu-content">
				<div class="desc" v-t="'meetings_record_cloud_description'" />
				<Button
					class="start-button"
					danger
					@click="updateRecording()"
					:loading="isLoading"
				>
					{{ buttonText }}
					<div v-if="isRecording && !isLoading" v-text="timerText" />
				</Button>
				<div
					v-if="startedByUser"
					class="started_by"
					v-text="
						$t('meetings_record_cloud_startedbyuser', [startedByUser.fullname])
					"
				/>
			</div>
		</template>
	</Popover>
</template>

<script>
import { Button, Popover } from '@/components/common'
import { post } from '../../helpers/api'
import moment from 'moment'
import { store, getUserById, getVideoSession, setVideoSession } from './data'
import { RecordVideoIcon } from '@/icons'
import { showAlert, hideAlert } from '@/components/Meeting/MeetingAlert'

export default {
	components: {
		Button,
		Popover,
		RecordVideoIcon,
	},

	props: ['isPublishing'],

	data() {
		return {
			timerText: null,
			recordingState: 'idle',
			recordingTimer: 0,
			timerInterval: null,
			showRecordMenu: false,
			activeArchive: null,
		}
	},

	mounted() {
		this.recordingTimer = 0
		this.activeArchive = this.getActiveArchive()
		if (this.activeArchive) {
			this.recordingState = 'recording'
			this.showRecordingAlert()
			const start = moment.utc(this.activeArchive.starttime)
			const now = moment.utc()
			this.recordingTimer = moment(now).diff(start, 'seconds')
			this.startTimer()
		}

		this.$onEventBus('videosessionUpdated', (videosession, previous) => {
			const archive = this.getActiveArchive()
			if (archive) {
				this.recordingState = 'recording'
				if (!this.recordingAlert && !this.activeArchive) {
					this.showRecordingAlert()
				}
				if (!this.timerInterval) {
					const start = moment.utc(archive.starttime)
					const now = moment.utc()
					this.recordingTimer = moment(now).diff(start, 'seconds')
					this.startTimer()
				}
			} else {
				this.recordingState = 'idle'
				this.stopTimer()
			}
			this.activeArchive = archive
		})
	},

	computed: {
		isRecording() {
			return this.recordingState === 'recording'
		},

		isLoading() {
			return ['starting', 'stopping'].includes(this.recordingState)
		},

		buttonText() {
			switch (this.recordingState) {
				case 'starting':
					return this.$t('meetings_record_starting')
				case 'recording':
					return this.$t('common_button_stop')
				case 'stopping':
					return this.$t('meetings_record_saving')
				default:
					return this.$t('common_button_record')
			}
		},

		startedByUser() {
			const archive = this.getActiveArchive()
			if (!archive) return null
			const user = getUserById(archive.start_user_id)
			return user
		},
	},

	methods: {
		showRecordingAlert() {
			if (this.recordingAlert) {
				hideAlert(this.recordingAlert)
			}
			this.recordingAlert = showAlert({
				text: this.$t('meetings_action_message_recording'),
				error: true,
				time: 4000,
			})
			setTimeout(() => {
				this.recordingAlert = null
			}, 4000)
		},

		incrementTimer() {
			if (this.recordingState === 'recording') {
				this.recordingTimer++
				const time = moment.utc(this.recordingTimer * 1000).format('HH:mm:ss')
				this.timerText = time
			}
		},

		updateRecording() {
			let post_data = {
				bubble_id: parseInt(store.bubble.id),
				videosession_id: getVideoSession().id,
			}

			if (this.recordingState === 'idle') {
				this.recordingState = 'starting'
			} else if (this.recordingState === 'recording') {
				this.recordingState = 'stopping'
			}

			const nextState = ['recording', 'stopping'].includes(this.recordingState)
				? 'stop'
				: 'start'

			if (nextState === 'start') {
				this.startTimer()
			}

			post(`/api/v1/videosession.${nextState}archive`, post_data)
				.then(({ data }) => {
					this.recordingState = nextState === 'start' ? 'recording' : 'idle'
					setVideoSession(data.videosession)
					setTimeout(this.closeDropdown, 1500)

					if (nextState === 'stop') {
						this.stopTimer()
					}
				})
				.catch((error) => {
					console.error(error)
					this.recordingState = 'idle'
				})
		},

		startTimer() {
			this.timerText = '00:00'
			this.timerInterval = setInterval(this.incrementTimer, 1000)
		},

		stopTimer() {
			this.timerText = null
			this.recordingTimer = 0
			clearInterval(this.timerInterval)
			this.timerInterval = null
		},

		closeDropdown() {
			this.$refs.popover?.closePopover?.()
		},

		getActiveArchive() {
			const session = getVideoSession()
			if (!session || !session.archives?.length) return null
			return session.archives.find((a) => a.status === 'RECORDING')
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

$red: #ff525a;

.record-button {
	height: 26px;
	padding: 1px 8px 4px 8px;
	border-radius: 18px !important;
	background-color: #38393c;
	cursor: pointer;
	outline: none;
}

.record-icon,
.record-text {
	display: inline-block;
}

.record-text {
	font-size: 12px;
	color: #ffffff;
}

.record-icon {
	width: 19px;
	height: 12px;
	vertical-align: middle;
	margin-right: 4px;
}

.recording {
	color: white !important;
	background-color: $red !important;
}

.menu-content {
	width: 400px;
	padding: 24px 32px;
	margin-top: 4px;
	@include flex-column-center(16px);
	background: var(--neutral-200);
	border-radius: 10px;
	.desc {
		text-align: center;
	}
	.started_by {
		font-size: 12px;
	}
	.start-button {
		@include flex(6px);
		background-color: $red;
	}
}
</style>
